import type { IconName } from "./Icon";

// Exported so the heading test can be driven off this list rather than a
// hand-written copy of it: a route added here is covered without anyone
// remembering to extend the test.
export const SECTIONS = [
	// Basket, not ChestTreasure: the brand doc's mapping named ChestTreasure
	// for the approved pool, but it measures 9% legible against the night
	// panel this renders on. See onlooker-1kr, and the amendment on the
	// 2026-08-11 brand spec.
	{ to: "/lessons", label: "Lessons", icon: "Basket" },
	{ to: "/machines", label: "Machines", icon: "Key" },
	// Book: the log-shaped icon in the brand set, and the one not already
	// spoken for by lessons, machines, settings or profile.
	{ to: "/activity", label: "Activity", icon: "Book" },
	// Monitor: a session summary is time spent on a machine's screen, and
	// nothing else in the set names that. Clears the coverage floor with
	// margin on every moving ground (52%/47%/53%/47% against night panel,
	// day panel, night ground, day ground - all comfortably over the 25%
	// floor in packages/brand/assets.test.ts), and is not already used by
	// another section.
	{ to: "/sessions", label: "Sessions", icon: "Monitor" },
	// Document, not the By repository panel's Briefcase: Briefcase measures 5%
	// on the night panel against the 25% floor in packages/brand/assets.test.ts,
	// and Document clears it (48% at worst).
	{ to: "/projects", label: "Projects", icon: "Document" },
	// Home over Letter: Letter already passes the contrast floor with no
	// brand change needed, but it reads as mail - and this page is more than
	// its invitation flow, so a mail icon would mislabel it. Home costs one
	// line in packages/brand's UNPLATED_ICONS and does not carry that
	// reading. Placed after Sessions and before Settings - account-shaped
	// rather than data-shaped, the same half of the nav as Settings/Profile.
	{ to: "/orgs", label: "Orgs", icon: "Home" },
	{ to: "/settings", label: "Settings", icon: "Gear" },
	// CatHead is an extension of the brand doc's mapping, not one of its
	// entries - the set has no person icon, and it is the most person-like
	// thing in it. See the doc's Icons section.
	{ to: "/profile", label: "Profile", icon: "CatHead" },
] as const satisfies readonly { to: string; label: string; icon: IconName }[];

/**
 * Which sections the header treats as account rather than product.
 *
 * A presentation split, derived here rather than by reordering SECTIONS or
 * giving it a `group` field. SECTIONS is also what `sectionFor` matches paths
 * against and what shell-headings.test.tsx drives its cases from, so its order
 * and shape answer questions that have nothing to do with where a link sits in
 * the header - and a reorder for layout's sake would quietly change both.
 *
 * The split follows the design this header was taken from, which puts what the
 * product does on one side and who you are on the other. See ONL-178. Orgs
 * joined the account side in ONL-218, when that side became a dropdown under
 * the signed-in person's name: which orgs you belong to is about who you are.
 */
export const ACCOUNT_SECTION_PATHS: readonly string[] = [
	"/orgs",
	"/settings",
	"/profile",
];

/**
 * Which section a path belongs to, or undefined for a path outside the shell.
 *
 * Exact match or a `to`-plus-slash prefix, so /lessons/:id resolves to Lessons.
 * Prefix alone would be wrong in principle - /lessons would also match a future
 * /lessons-archive - and the slash costs nothing.
 *
 * Lives here rather than inside AppShell because titles.ts asks the same
 * question. Two copies of this rule is exactly the drift that onlooker-eqb was
 * filed about, one level down.
 */
export function sectionFor(
	pathname: string,
): (typeof SECTIONS)[number] | undefined {
	return SECTIONS.find(
		(candidate) =>
			pathname === candidate.to || pathname.startsWith(`${candidate.to}/`),
	);
}
